/**
 * THE port. The React layer codes against this interface and never learns
 * whether a backend exists (plan §1.1 · architecture §0).
 *
 * Five rules keep that real rather than aspirational, and three of them are
 * visible in this file:
 *
 *  1. Every method returns `Promise<T>` EVEN IN LOCAL MODE. A synchronous
 *     `LocalRepository.getDashboard()` would let a component infer the adapter
 *     from its own call site, and the inference would be correct — which is
 *     what makes it fatal. `LocalRepository` therefore wraps pure, already
 *     -computed values in resolved promises on purpose.
 *  2. Every `T` is inferred from a Zod schema in `@meridian/contracts`, so local
 *     and HTTP return the SAME TypeScript symbol rather than two similar ones.
 *     That is why nothing in this file declares a payload shape of its own.
 *  3. Adapter selection happens once at boot from a Zod-validated
 *     `VITE_DATA_SOURCE`; production has no default (plan §1.1 rule 4).
 *
 * The two implementations are `LocalRepository` (this package,
 * `src/adapters/`) and `HttpRepository` (`apps/web/src/data/`) — never
 * `ApiRepository`: name the TRANSPORT, never the backend, or the one symbol in
 * a package forbidden to know an API exists would be named after one
 * (naming §3.5).
 *
 * Query parameters use the API vocabulary (`window`, `index`), not the URL
 * vocabulary (`win`, `idx`). The two are deliberately different and the mapping
 * happens once, in the web query hooks (ADR-0003 · plan §6.6 traps).
 */
import type {
  DashboardPayload,
  DashboardQuery,
  DatasetMeta,
  DiversificationPayload,
  PriceSeries,
  SearchQuery,
  SectorDetail,
  SectorId,
  SectorQuery,
  Security,
  SecurityProfile,
  SeriesQuery,
  Ticker,
} from '@meridian/contracts';

/**
 * Raised when an id does not resolve. Both adapters reject with this rather
 * than resolving an empty payload, so `apps/api` can map it to a 404 and the
 * SPA can render "not found" instead of an empty screen (plan §6.6: unknown
 * ticker/sector → 404, never an empty 200).
 */
/** Closed vocabulary for the resource a missing id refers to. */
export type DatasetResource = 'security' | 'sector';

export class DatasetNotFoundError extends Error {
  /** Discriminant, so a catch site does not depend on `instanceof` surviving a bundler. */
  public readonly kind = 'dataset-not-found' as const;

  public constructor(
    public readonly resource: DatasetResource,
    public readonly id: string,
  ) {
    super(`${resource} not found: ${id}`);
    this.name = 'DatasetNotFoundError';
  }
}

/**
 * Realm-safe guard: require the discriminant AND resource/id strings so a bare
 * `{ kind: 'dataset-not-found' }` does not type-narrow to a full error with
 * undefined fields at the HTTP 404 mapping site.
 */
export function isDatasetNotFoundError(error: unknown): error is DatasetNotFoundError {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { kind?: unknown; resource?: unknown; id?: unknown };
  return (
    e.kind === 'dataset-not-found' && typeof e.resource === 'string' && typeof e.id === 'string'
  );
}

export interface DatasetRepository {
  /** Freshness oracle. Local mode reports `synthetic` with null ingest state. */
  readonly getMeta: () => Promise<DatasetMeta>;

  /** Composite dashboard payload — rows, sector aggregates and breadth in one. */
  readonly getDashboard: (query: DashboardQuery) => Promise<DashboardPayload>;

  /** Sector screen: KPIs, members, movers, correlation matrix, diversifiers. */
  readonly getSector: (id: SectorId, query: SectorQuery) => Promise<SectorDetail>;

  /** Stock screen: row, 52w stats, `consensus | null`, `catalysts[]`. */
  readonly getSecurity: (ticker: Ticker) => Promise<SecurityProfile>;

  /** Complete bar set for the window — decimation is the chart's business. */
  readonly getSeries: (ticker: Ticker, query: SeriesQuery) => Promise<PriceSeries>;

  /** Same-sector leaders by return; the subject appears only if it ranks. */
  readonly getPeers: (ticker: Ticker, query: SeriesQuery) => Promise<readonly Security[]>;

  /** Three mutually exclusive groups plus the correlation extremes. */
  readonly getDiversifiers: (ticker: Ticker, query: SeriesQuery) => Promise<DiversificationPayload>;

  /**
   * The ONLY typeahead path in both modes. `HttpRepository` prefetches the
   * universe once per `asOf` and delegates to the same `core` selector, so the
   * §6.6 ordering contract is provable by shared code rather than only tested
   * by parity fixtures.
   */
  readonly search: (query: SearchQuery) => Promise<readonly Security[]>;
}
